import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { storageManager } from '~/shared/lib/storage-manager'

let bypassOn = false

interface AdminBypassContextValue {
  isAdminViewing: boolean
  isBypassing: boolean
  enableBypass: () => void
  disableBypass: () => void
}

const AdminBypassContext = createContext<AdminBypassContextValue | null>(null)

/**
 * Header attached to API requests so the backend can honor the admin bypass.
 * The backend only acts on it when the caller's JWT carries the global_admin
 * role, so a non-admin sending it gains nothing.
 */
export function adminBypassHeaders(): Record<string, string> {
  if (!storageManager.isAdmin()) return {}
  return bypassOn ? { 'X-Admin-Bypass': 'true' } : {}
}

export function resetBypass(): void {
  bypassOn = false
}

export function AdminBypassProvider({ children }: { children: React.ReactNode }) {
  const isAdminViewing = storageManager.isAdmin() && !!storageManager.getAdminViewingBusinessId()
  const businessId = storageManager.getAdminViewingBusinessId()

  const [isBypassing, setIsBypassing] = useState(false)

  useEffect(() => {
    bypassOn = false
    setIsBypassing(false)
  }, [businessId])

  const enableBypass = useCallback(() => {
    bypassOn = true
    setIsBypassing(true)
  }, [])

  const disableBypass = useCallback(() => {
    bypassOn = false
    setIsBypassing(false)
  }, [])

  const value = useMemo<AdminBypassContextValue>(
    () => ({
      isAdminViewing,
      isBypassing: isAdminViewing && isBypassing,
      enableBypass,
      disableBypass,
    }),
    [isAdminViewing, isBypassing, enableBypass, disableBypass],
  )

  return <AdminBypassContext.Provider value={value}>{children}</AdminBypassContext.Provider>
}

export function useAdminBypass(): AdminBypassContextValue {
  const ctx = useContext(AdminBypassContext)
  if (!ctx) {
    throw new Error('useAdminBypass must be used within AdminBypassProvider')
  }
  return ctx
}
